import { useEffect, useMemo, useState } from 'react';
import { SaveOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Stack } from '@mui/material';
import {
  CompactEditButton,
  DefinitionList,
  FormTextField,
  FeedbackSnackbar,
  EditPage,
  PageSections,
  ResourceState,
  SectionCard,
  designTokens,
  useConfirmation,
} from '../../../design-system';
import { CertificateSheet } from '../components/CertificateSheet';
import { PolicySwitch } from '../components/PolicySwitch';
import { certificateTemplates } from '../fixtures/certificateFixtures';
import {
  certificateSettingSaveError,
  certificateTemplatePolicyKey,
  certificateTemplatePolicyErrors,
  isCertificateVersionConflict,
} from '../model/presentation';
import type { CertificateAccessProps, CertificateTemplate } from '../model/types';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import type { CertificateSettingsPort } from '../port/CertificatesPort';

export function CertificateSettingsPage({
  port,
  fixtureMode,
  readOnly,
  scopes,
  onOpenCompanySettings,
}: {
  port: CertificateSettingsPort;
  fixtureMode: boolean;
  onOpenCompanySettings: () => void;
} & CertificateAccessProps) {
  const canRead = scopes.includes('certificates.settings.read');
  const canWrite = canRead && !readOnly && scopes.includes('certificates.settings.write');
  const canOpenBasicSettings = scopes.includes('tenant.settings.read');
  const resource = useAsyncResource(
    () => fixtureMode ? Promise.resolve(certificateTemplates) : port.listCertificateSettings(),
    [port, fixtureMode],
  );
  const [savedTemplates, setSavedTemplates] = useState<CertificateTemplate[]>();
  const templates = savedTemplates ?? resource.data ?? [];
  const [selectedType, setSelectedType] = useState<CertificateTemplate['certificateType']>('EMPLOYMENT');
  const selected = templates.find((item) => item.certificateType === selectedType);
  const [draft, setDraft] = useState<CertificateTemplate>(certificateTemplates[0]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<'template' | 'seal' | 'policy'>();
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  const { confirm, confirmationDialog } = useConfirmation();

  useEffect(() => { if (resource.data) setSavedTemplates(resource.data); }, [resource.data]);
  useEffect(() => { if (selected) setDraft(selected); setEditing(undefined); }, [selected]);

  const busy = saving || resource.loading;

  const dirty = useMemo(
    () => selected ? certificateTemplatePolicyKey(draft) !== certificateTemplatePolicyKey(selected) : false,
    [draft, selected],
  );
  const fieldErrors = certificateTemplatePolicyErrors(draft);
  const valid = !Object.values(fieldErrors).some(Boolean);
  const updateDraft = (patch: Partial<CertificateTemplate>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setNotice(undefined);
  };
  const selectType = async (type: CertificateTemplate['certificateType']) => {
    if (busy || type === selectedType) return;
    if (dirty && !await confirm({
      intent: 'discard',
      title: '수정한 내용을 버릴까요?',
      description: `“${selected?.displayName ?? '증명서'}”의 저장하지 않은 변경을 버리고 다른 증명서 설정으로 이동합니다.`,
      confirmLabel: '버리고 이동',
      cancelLabel: '계속 수정',
    })) return;
    setSelectedType(type);
    setEditing(undefined);
    setError(undefined);
    setNotice(undefined);
  };

  const save = () => {
    if (!canWrite || !selected || !dirty || !valid || busy || resource.error) return;
    setSaving(true);
    setNotice(undefined);
    setError(undefined);
    const promise = fixtureMode
      ? Promise.resolve({ ...draft, version: draft.version + 1, publishedAt: new Date().toISOString() })
      : port.updateCertificateSetting(selectedType, draft);
    void promise.then((item) => {
      setSavedTemplates((current) => (current ?? templates).map((template) => template.certificateType === item.certificateType ? item : template));
      setDraft(item);
      setEditing(undefined);
      setNotice(`${item.displayName} 버전 ${item.version}을 게시했습니다.`);
    }, (cause: unknown) => {
      setError(certificateSettingSaveError(cause));
      if (isCertificateVersionConflict(cause)) resource.reload();
    }).finally(() => setSaving(false));
  };

  return <><EditPage
    title="증명서 설정"
    help="변경 사항은 새 버전으로 게시되어 이후 발급부터 적용됩니다. 기존 발급 문서와 진위확인 정보는 유지됩니다."
    width="wide"
    editActions={canWrite ? <>
      <Button variant="outlined" disabled={!selected || !dirty || busy || Boolean(resource.error)} onClick={() => {
        if (busy || resource.error || !selected) return;
        setDraft(selected);
        setEditing(undefined);
        setNotice(undefined);
        setError(undefined);
      }}>취소</Button>
      <Button variant="contained" startIcon={<SaveOutlined />} disabled={!dirty || !valid || busy || Boolean(resource.error)} onClick={save}>{saving ? '게시 중…' : '새 버전 게시'}</Button>
    </> : undefined}
  >
    <ResourceState loading={resource.loading} error={resource.error?.message} empty={!templates.length} onRetry={resource.reload}>
      <PageSections>
        {error && <Alert severity="error">{error}</Alert>}
        {dirty && !valid && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => setEditing(fieldErrors.displayName || fieldErrors.statement ? 'template' : 'policy')}>입력 확인</Button>}>입력한 내용을 확인한 뒤 게시해 주세요.</Alert>}
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }} aria-label="증명서 종류">
          {templates.map((template) => <Button key={template.certificateType} variant={selectedType === template.certificateType ? 'weak' : 'outlined'} disabled={busy} aria-pressed={selectedType === template.certificateType} onClick={() => selectType(template.certificateType)} endIcon={<Chip size="small" label={`v${template.version}`} />}>{template.displayName}</Button>)}
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: `${designTokens.layout.pageSectionGap}px`, alignItems: 'start' }}>
          <PageSections>
            <SectionCard title="증명서 양식" help="발급 대상 구성원의 소속 법인에 등록된 법인명·본점 주소·대표자·직인을 발급 시점에 가져옵니다." bodyPadding="standard" action={canWrite ? <CompactEditButton aria-label="증명서 양식 수정" disabled={busy} onClick={() => setEditing(editing === 'template' ? undefined : 'template')}>{editing === 'template' ? '접기' : '수정'}</CompactEditButton> : undefined}>
              <Stack spacing={2}>
                {editing === 'template' ? <>
                  <FormTextField required label="표시 이름" value={draft.displayName} disabled={saving} error={Boolean(fieldErrors.displayName)} helperText={fieldErrors.displayName} onChange={(event) => updateDraft({ displayName: event.target.value })} slotProps={{ htmlInput: { maxLength: 80 } }} />
                  <FormTextField required label="확인 문구" value={draft.statement} characterCount={{ current: draft.statement.length, max: 500 }} disabled={saving} error={Boolean(fieldErrors.statement)} helperText={fieldErrors.statement} onChange={(event) => updateDraft({ statement: event.target.value })} multiline minRows={3} slotProps={{ htmlInput: { maxLength: 500 } }} />
                </> : <DefinitionList items={[["표시 이름", draft.displayName], ["확인 문구", draft.statement]]} />}
                {canOpenBasicSettings ? <Box><Button variant="outlined" size="small" disabled={saving} onClick={onOpenCompanySettings}>계열사 관리로 이동</Button></Box> : null}
              </Stack>
            </SectionCard>
            <Box data-testid="certificate-seal-policy">
              <SectionCard title="직인" help="법인별 직인 등록과 변경은 회사 직인에서 관리합니다." bodyPadding="standard" action={canWrite ? <CompactEditButton aria-label="직인 정책 수정" disabled={busy} onClick={() => setEditing(editing === 'seal' ? undefined : 'seal')}>{editing === 'seal' ? '접기' : '수정'}</CompactEditButton> : undefined}>
                <Stack spacing={1.5}>
                  {editing === 'seal' ? <PolicySwitch title="직인 필수" description="직인이 등록된 경우에만 이 증명서를 발급합니다." checked={draft.requireSeal} disabled={saving} onChange={(checked) => updateDraft({ requireSeal: checked })} /> : <DefinitionList items={[["발급 조건", draft.requireSeal ? '직인이 등록된 법인만 발급 가능' : '직인이 없어도 발급 가능']]} />}
                </Stack>
              </SectionCard>
            </Box>
            <SectionCard title="발급 정책" bodyPadding="standard" action={canWrite ? <CompactEditButton aria-label="발급 정책 수정" disabled={busy} onClick={() => setEditing(editing === 'policy' ? undefined : 'policy')}>{editing === 'policy' ? '접기' : '수정'}</CompactEditButton> : undefined}>
              {editing === 'policy' ? <Stack spacing={2}>
                <PolicySwitch title="직원에게 제공" description="발급 가능한 증명서 목록에 표시합니다." checked={draft.enabled} disabled={saving} onChange={(checked) => updateDraft({ enabled: checked })} />
                <PolicySwitch title="자동 발급" description="표준 요청을 HR 승인 없이 발급합니다." checked={draft.autoIssue} disabled={saving} onChange={(checked) => updateDraft({ autoIssue: checked })} />
                <PolicySwitch title="외부 진위확인" description="QR 링크에서 최소 정보를 공개합니다." checked={draft.verificationPublic} disabled={saving} onChange={(checked) => updateDraft({ verificationPublic: checked })} />
                <FormTextField required type="number" label="진위확인 유효기간" value={draft.validityDays} disabled={saving} error={Boolean(fieldErrors.validityDays)} onChange={(event) => updateDraft({ validityDays: Number(event.target.value) })} helperText={fieldErrors.validityDays ?? '1~3650일'} help="발급일 이후 외부 진위확인에 적용되며, 발급한 PDF는 유지됩니다." slotProps={{ htmlInput: { min: 1, max: 3650, step: 1 } }} />
              </Stack> : <DefinitionList items={[["직원에게 제공", draft.enabled ? '제공' : '제공하지 않음'], ["발급 방식", draft.autoIssue ? '신청 즉시 자동 발급' : 'HR 확인 후 발급'], ["외부 진위확인", draft.verificationPublic ? `발급일로부터 ${draft.validityDays}일 동안 제공` : '제공하지 않음']]} />}
            </SectionCard>
          </PageSections>
          <SectionCard help="이름·회사·직인은 예시입니다. 실제 발급 시 구성원과 소속 법인의 정보로 채워집니다." title={<Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><span>발급 문서 예시</span>{dirty && <Chip size="small" label="게시 전 변경" />}</Stack>} bodyPadding="standard">
            <Stack spacing={2}>
              <CertificateSheet purposeCode="OTHER" destination="제출처" preview={{ template: draft, snapshot: {
                legalName: '구성원 이름', maskedRegistrationNumber: '******-*******', workerCategory: '직원 유형', workerNumber: '',
                companyId: '', companyLegalProfileId: '', companyLegalProfileVersion: 0,
                companyName: '소속 법인명', registeredAddress: '법인에 등록된 본점 주소', representativeTitle: '대표자', representativeName: '대표자 이름', sealConfigured: draft.requireSeal,
                organizationName: '소속 조직', businessTitle: '직책', hireDate: '2024-01-01', workerStatus: 'ACTIVE', issuedOn: new Date().toISOString().slice(0, 10),
              } }} />
            </Stack>
          </SectionCard>
        </Box>
      </PageSections>
    </ResourceState>
  </EditPage>
    <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
    {confirmationDialog}
  </>;
}
